import type {
  DrillAdvancedFiltersPayload,
  DrillChecksPayload,
  DrillResponseDto,
  DrillSimpleFiltersPayload,
  DrillTaxonomyResponseDto,
  ExportSelectionArgs,
  GetDrillArgs,
  GetDrillTaxonomyArgs,
} from "@/@types/price-markdown-selection";
import { Tags } from "@/enum/tags";
import { apiSlice } from "@/services/http";

import { ITEM_STATUS_FILTER, TRI_STATE_FILTER } from "./drill-filters";
import { withQueryString } from "./query-string";

function appendChecks(
  params: URLSearchParams,
  checks?: DrillChecksPayload,
): void {
  if (!checks) return;
  if (
    checks.seasonalFilter !== undefined &&
    checks.seasonalFilter !== TRI_STATE_FILTER.TODOS
  ) {
    params.set("seasonalFilter", checks.seasonalFilter);
  }
  if (
    checks.kviFilter !== undefined &&
    checks.kviFilter !== TRI_STATE_FILTER.TODOS
  ) {
    params.set("kviFilter", checks.kviFilter);
  }
  if (
    checks.backlogFilter !== undefined &&
    checks.backlogFilter !== TRI_STATE_FILTER.TODOS
  ) {
    params.set("backlogFilter", checks.backlogFilter);
  }
  if (
    checks.repricedFilter !== undefined &&
    checks.repricedFilter !== TRI_STATE_FILTER.TODOS
  ) {
    params.set("repricedFilter", checks.repricedFilter);
  }
  if (
    checks.itemStatusFilter !== undefined &&
    checks.itemStatusFilter !== ITEM_STATUS_FILTER.TODAS
  ) {
    params.set("itemStatusFilter", checks.itemStatusFilter);
  }
  if (checks.negativeMarginOnly !== undefined) {
    params.set("negativeMarginOnly", String(checks.negativeMarginOnly));
  }
  if (checks.markedOnly !== undefined) {
    params.set("markedOnly", String(checks.markedOnly));
  }
  if (checks.suggestedOnly !== undefined) {
    params.set("suggestedOnly", String(checks.suggestedOnly));
  }
}

function appendList(
  params: URLSearchParams,
  optionKey: string,
  values: string[] | undefined,
): void {
  if (values === undefined) return;
  if (values.length === 0) {
    params.append(optionKey, "");
    return;
  }
  values.forEach((v) => params.append(optionKey, v));
}

function appendSimpleFilters(
  params: URLSearchParams,
  filters?: DrillSimpleFiltersPayload,
): void {
  if (!filters) return;
  appendList(params, "matrix", filters.matrix);
  appendList(params, "stCategory", filters.stCategory);
  appendList(params, "ageBucket", filters.ageBucket);
  appendList(params, "sourceFamily", filters.sourceFamily);
  appendList(params, "department", filters.department);
  appendList(params, "group", filters.group);
  appendList(params, "subgroup", filters.subgroup);
  if (filters.code) params.set("code", filters.code);
}

function appendAdvancedFilters(
  params: URLSearchParams,
  filters?: DrillAdvancedFiltersPayload,
): void {
  if (!filters) return;
  if (filters.description) params.set("description", filters.description);
  if (filters.ageFrom !== undefined) {
    params.set("ageFrom", String(filters.ageFrom));
  }
  if (filters.ageTo !== undefined) {
    params.set("ageTo", String(filters.ageTo));
  }
  if (filters.stLessThan !== undefined) {
    params.set("stLessThan", String(filters.stLessThan));
  }
  if (filters.stockFrom !== undefined) {
    params.set("stockFrom", String(filters.stockFrom));
  }
  if (filters.stockTo !== undefined) {
    params.set("stockTo", String(filters.stockTo));
  }
  if (filters.lastRepricingFrom) {
    params.set("lastRepricingFrom", filters.lastRepricingFrom);
  }
  if (filters.lastRepricingTo) {
    params.set("lastRepricingTo", filters.lastRepricingTo);
  }
}

export const priceMarkdownDrillApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDrill: builder.query<DrillResponseDto, GetDrillArgs>({
      query: ({
        markdownId,
        family,
        cycle,
        page,
        perPage,
        orderBy,
        simpleFilters,
        checks,
        advancedFilters,
      }) => {
        const params = new URLSearchParams();
        if (family) params.set("family", family);
        if (cycle) params.set("cycle", cycle);
        if (page !== undefined) params.set("page", String(page));
        if (perPage !== undefined) params.set("perPage", String(perPage));
        if (orderBy) params.set("orderBy", orderBy);
        appendSimpleFilters(params, simpleFilters);
        appendChecks(params, checks);
        appendAdvancedFilters(params, advancedFilters);
        return {
          url: withQueryString(`/price-markdown/selection/drill/${markdownId}`, params),
          method: "GET",
        };
      },
      providesTags: (_result, _error, { markdownId }) => [
        { type: Tags.PRICE_MARKDOWN_DRILL, id: markdownId },
        Tags.PRICE_MARKDOWN_DRILL,
      ],
    }),

    getDrillTaxonomy: builder.query<
      DrillTaxonomyResponseDto,
      GetDrillTaxonomyArgs
    >({
      query: ({ family, department, group, sourceFamily }) => {
        const params = new URLSearchParams();
        params.set("family", family);
        appendList(params, "department", department);
        appendList(params, "group", group);
        appendList(params, "sourceFamily", sourceFamily);
        return {
          url: `/price-markdown/selection/drill-taxonomy?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.PRICE_MARKDOWN_DRILL],
    }),

    exportSelection: builder.mutation<Blob, ExportSelectionArgs>({
      query: ({
        markdownId,
        family,
        cycle,
        simpleFilters,
        checks,
        advancedFilters,
      }) => {
        let markdownRef: { markdownId?: string } = {};
        if (markdownId) {
          markdownRef = { markdownId };
        }
        return {
          url: `/price-markdown/selection/export`,
          method: "POST",
          body: {
            ...markdownRef,
            family,
            cycle,
            ...simpleFilters,
            ...checks,
            ...advancedFilters,
          },
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDrillQuery,
  useGetDrillTaxonomyQuery,
  useExportSelectionMutation,
} = priceMarkdownDrillApi;
